import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Save, Images, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';
import ImageUpload from './ImageUpload';

const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `slide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const SliderManager = () => {
  const { settings, updateSettings } = useSettings();
  const [slides, setSlides] = useState(settings.slider_images || []);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState({ image_url: '', title: '', subtitle: '' });

  const persist = async (next) => {
    setSaving(true);
    try {
      await updateSettings({ slider_images: next });
      setSlides(next);
      toast.success('Slider berhasil diperbarui!');
    } catch (error) {
      toast.error('Gagal menyimpan slider');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!draft.image_url) {
      toast.error('Silakan upload foto terlebih dahulu');
      return;
    }
    const next = [...slides, { id: genId(), ...draft }];
    await persist(next);
    setDraft({ image_url: '', title: '', subtitle: '' });
    setShowModal(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus foto slider ini?')) return;
    await persist(slides.filter((s) => s.id !== id));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center">
            <Images className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-bold text-primary-dark">Image Slider Halaman Depan</h3>
            <p className="text-sm font-body text-text-secondary">Tambah dan kelola foto carousel di Home</p>
          </div>
        </div>
        <button
          data-testid="add-slide-btn"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Tambah Foto
        </button>
      </div>

      {slides.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Images className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body">Belum ada foto slider</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {slides.map((slide) => (
            <div
              key={slide.id}
              data-testid={`slide-item-${slide.id}`}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
            >
              <div
                className="h-36 bg-cover bg-center"
                style={{ backgroundImage: `url('${slide.image_url}')` }}
              />
              <div className="p-4">
                <h4 className="font-body font-semibold text-text-primary truncate">
                  {slide.title || 'Tanpa judul'}
                </h4>
                <p className="text-sm font-body text-text-secondary truncate">{slide.subtitle}</p>
                <button
                  data-testid={`delete-slide-${slide.id}`}
                  onClick={() => handleDelete(slide.id)}
                  disabled={saving}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-body font-medium transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-heading font-bold text-primary-dark">Tambah Foto Slider</h3>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-stone-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <ImageUpload
                  value={draft.image_url}
                  onChange={(url) => setDraft({ ...draft, image_url: url })}
                  label="Foto Slider"
                />
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">Judul (opsional)</label>
                  <input
                    type="text"
                    data-testid="slide-title-input"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Kajian Subuh Akbar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">Subjudul (opsional)</label>
                  <input
                    type="text"
                    data-testid="slide-subtitle-input"
                    value={draft.subtitle}
                    onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Setiap Ahad pagi pukul 05.30"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    data-testid="slide-submit-btn"
                    onClick={handleAdd}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors disabled:opacity-50"
                  >
                    <Save className="w-5 h-5" />
                    Simpan
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SliderManager;
